/**
 * Toast Container Component
 *
 * Renders all active toast notifications in a fixed position.
 * Supports different toast types with appropriate styling and icons.
 */

import { CircleAlert, CircleCheckBig, Info, TriangleAlert, X } from 'lucide-react';
import { useToast, type Toast } from '../../contexts/ToastContext';
import { cn } from '@/lib/utils';

const toastStyles = {
  success: {
    className: 'border-success/20 bg-success/10 text-success',
    icon: <CircleCheckBig className="h-5 w-5" />,
    label: 'Success',
  },
  error: {
    className: 'border-destructive/20 bg-destructive/8 text-destructive',
    icon: <CircleAlert className="h-5 w-5" />,
    label: 'Error',
  },
  warning: {
    className: 'border-warning/20 bg-warning/10 text-warning',
    icon: <TriangleAlert className="h-5 w-5" />,
    label: 'Warning',
  },
  info: {
    className: 'border-info/20 bg-info/10 text-info',
    icon: <Info className="h-5 w-5" />,
    label: 'Info',
  },
};

function ToastItem({ toast }: { toast: Toast }) {
  const { dismissToast } = useToast();
  const style = toastStyles[toast.type];
  // Error toasts announce assertively; success/info/warning stay polite so
  // they don't interrupt the user. role="alert" implies assertive live region,
  // role="status" implies polite.
  const isAssertive = toast.type === 'error';

  return (
    <div
      className={cn(
        'surface-panel-strong pointer-events-auto mb-3 flex min-w-[320px] max-w-md items-start gap-3 rounded-card border px-4 py-4 shadow-[0_24px_60px_-32px_hsl(var(--foreground)/0.28)] backdrop-blur-xl animate-in slide-in-from-right',
        style.className
      )}
      role={isAssertive ? 'alert' : 'status'}
      aria-live={isAssertive ? 'assertive' : 'polite'}
    >
      <div className="shrink-0">{style.icon}</div>
      <div className="flex-1">
        <div className="text-[11px] font-semibold uppercase tracking-[0.18em] opacity-80">{style.label}</div>
        <div className="mt-1 text-sm leading-6">{toast.message}</div>
      </div>
      <button
        onClick={() => dismissToast(toast.id)}
        className="shrink-0 rounded-xl p-1 transition-colors hover:bg-foreground/10"
        aria-label="Dismiss"
      >
        <X className="h-4 w-4" />
      </button>
    </div>
  );
}

export function ToastContainer() {
  const { toasts } = useToast();

  if (toasts.length === 0) {
    return null;
  }

  return (
    <div className="pointer-events-none fixed bottom-4 right-4 z-50 w-[min(100vw-1.5rem,24rem)] sm:bottom-auto sm:top-4 sm:w-auto">
      {toasts.map((toast) => (
        <ToastItem key={toast.id} toast={toast} />
      ))}
    </div>
  );
}
